import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Loader2, Camera, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';
import { resolvePlaybackUrl, useHlsVideo, resolveThumbnailUrl } from '../../utils/hlsPlayer';
import { GENRES } from './studioShared';

// Lets the artist re-fill / correct title, description and genre on a video
// they already own. Built because the cleanup-orphans incident left some
// restored Bunny videos without metadata (Bunny only persists the title) —
// rather than wait for an admin to refill it, artists self-serve here.
export const EditDetailsModal = ({ open, video, axiosInstance, onClose, onSaved }) => {
  const [title, setTitle] = useState(video?.title || '');
  const [description, setDescription] = useState(video?.description || '');
  const [genre, setGenre] = useState(video?.genre || '');
  // Snapshot the original values so we can PATCH only the fields the user
  // actually changed — important because we coerce unknown legacy genres
  // (e.g. 'Electronic' from the old taxonomy) to 'Other' in the dropdown,
  // and we don't want a Save-without-touching-genre to silently overwrite
  // the stored value to 'Other'.
  const [original, setOriginal] = useState({ title: '', description: '', genre: '' });
  const [saving, setSaving] = useState(false);
  // Local thumbnail file the creator picked; null = keep existing. Preview
  // URL is a client-side blob URL for the newly-picked file only.
  const [newThumbFile, setNewThumbFile] = useState(null);
  const [newThumbPreview, setNewThumbPreview] = useState(null);
  const thumbInputRef = useRef(null);
  // Frame-capture surface — mirrors the upload page's "capture from video"
  // flow so creators can pick a moment from their actual video as the new
  // thumbnail, not just upload a stock image. Only rendered when the video
  // has a playable URL (approved + Bunny HLS ready, OR a legacy MP4 path).
  const videoPreviewRef = useRef(null);
  const canvasRef = useRef(null);
  const [previewActive, setPreviewActive] = useState(false);
  const [previewIsPlaying, setPreviewIsPlaying] = useState(false);
  const playbackUrl = video ? resolvePlaybackUrl(video) : null;
  // Only attach HLS when the mini-player is actually visible (previewActive).
  // Otherwise the modal downloads MB of HLS segments the user may never
  // watch, and hls.js pegs the CPU on iOS.
  useHlsVideo(videoPreviewRef, previewActive ? playbackUrl : null, { autoStart: false });

  useEffect(() => {
    if (open && video) {
      const t = video.title || '';
      const d = video.description || '';
      const g = (video.genre || '').trim();
      const coerced = GENRES.includes(g) ? g : (g ? 'Other' : '');
      setTitle(t);
      setDescription(d);
      setGenre(coerced);
      setOriginal({ title: t, description: d, genre: coerced });
      // Reset thumbnail picker whenever the modal is reopened for a fresh video.
      setNewThumbFile(null);
      setNewThumbPreview(null);
      setPreviewActive(false);
      setPreviewIsPlaying(false);
    }
  }, [open, video]);

  // Clean up the blob URL when the picked file changes or modal closes so
  // we don't leak object-URL entries.
  useEffect(() => {
    return () => {
      if (newThumbPreview) URL.revokeObjectURL(newThumbPreview);
    };
  }, [newThumbPreview]);

  if (!open || !video) return null;

  const pickThumbnail = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Pick an image (JPG, PNG, or WebP).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Thumbnail must be under 8 MB.');
      return;
    }
    if (newThumbPreview) URL.revokeObjectURL(newThumbPreview);
    setNewThumbFile(file);
    setNewThumbPreview(URL.createObjectURL(file));
  };

  const togglePreviewPlayback = () => {
    const el = videoPreviewRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => { /* best-effort, non-blocking */ });
      setPreviewIsPlaying(true);
    } else {
      el.pause();
      setPreviewIsPlaying(false);
    }
  };

  // Capture the current frame of the preview <video> element as the new
  // thumbnail. Ported (with the SAME iOS/Safari safeguards) from the upload
  // page's captureFrame — pixel-luminance black-frame detection, synchronous
  // dataURL→Blob conversion, and a seek-and-wait dance for decoders that
  // haven't populated the current frame yet.
  const captureCurrentFrame = async () => {
    const el = videoPreviewRef.current;
    const canvas = canvasRef.current;
    if (!el || !canvas) {
      toast.error('Preview not ready yet — hit Play first.');
      return;
    }
    const waitForFrame = () => new Promise((resolve) => {
      if (el.readyState >= 2) return resolve();
      const done = () => {
        el.removeEventListener('seeked', done);
        el.removeEventListener('loadeddata', done);
        resolve();
      };
      el.addEventListener('seeked', done, { once: true });
      el.addEventListener('loadeddata', done, { once: true });
      try { el.currentTime = el.currentTime; } catch (_e) { /* iOS teardown */ }
      setTimeout(done, 1200);
    });
    try { await waitForFrame(); } catch (_e) { /* fall through */ }
    const w = el.videoWidth || 1280;
    const h = el.videoHeight || 720;
    if (!w || !h) {
      toast.error("Couldn't read video frame yet — wait a moment and try again.");
      return;
    }
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    try {
      ctx.drawImage(el, 0, 0, w, h);
    } catch (_e) {
      toast.error("Couldn't capture this frame on mobile. Play the video for a second, pause, then capture.");
      return;
    }
    let dataUrl = '';
    try {
      dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    } catch (_e) {
      toast.error('Frame capture failed. Try a different point in the video.');
      return;
    }
    if (dataUrl.length < 5000) {
      toast.error("Frame looks empty — play briefly, pause, then capture.");
      return;
    }
    // Pixel-level black-frame check (mobile browsers often hand back a black
    // canvas until the video has actually played).
    try {
      const samples = 64;
      const data = ctx.getImageData(0, 0, w, h).data;
      let nonBlack = 0;
      let brightestSum = 0;
      for (let i = 0; i < samples; i += 1) {
        const px = Math.floor(Math.random() * (data.length / 4)) * 4;
        const lum = data[px] + data[px + 1] + data[px + 2];
        brightestSum = Math.max(brightestSum, lum);
        if (lum > 24) nonBlack += 1;
      }
      if (nonBlack < 2 && brightestSum < 24) {
        toast.error("Frame is all black — tap Play briefly first, pause on the frame you want, then capture.");
        return;
      }
    } catch (_e) { /* tainted canvas — ignore, safeguards above cover the obvious cases */ }
    // Synchronous dataURL → Blob conversion (do NOT switch to canvas.toBlob
    // async — the upload flow's race condition was exactly that).
    let capturedFile = null;
    try {
      const b64 = dataUrl.split(',')[1] || '';
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i += 1) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr], { type: 'image/jpeg' });
      capturedFile = new File([blob], `edit-frame-${Date.now()}.jpg`, { type: 'image/jpeg' });
    } catch (_e) {
      toast.error('Frame capture failed. Try a different point in the video.');
      return;
    }
    if (newThumbPreview) URL.revokeObjectURL(newThumbPreview);
    setNewThumbFile(capturedFile);
    setNewThumbPreview(URL.createObjectURL(capturedFile));
    toast.success(`Frame captured at ${Math.floor(el.currentTime)}s`);
  };

  const save = async () => {
    const t = (title || '').trim();
    if (!t) {
      toast.error('Title is required.');
      return;
    }
    if (!genre) {
      toast.error('Pick a genre.');
      return;
    }
    // Only send fields the user actually changed.
    const body = {};
    if (t !== (original.title || '').trim()) body.title = t;
    const d = (description || '').trim();
    if (d !== (original.description || '').trim()) body.description = d;
    if (genre !== original.genre) body.genre = genre;
    const hasTextChanges = Object.keys(body).length > 0;
    const hasThumbChange = !!newThumbFile;
    if (!hasTextChanges && !hasThumbChange) {
      toast.info('No changes to save.');
      onClose();
      return;
    }
    setSaving(true);
    try {
      // Send text updates + thumbnail in parallel so a slow thumbnail upload
      // doesn't block the text save (and a text failure doesn't prevent the
      // thumbnail from going up). Any single failure surfaces individually.
      const jobs = [];
      if (hasTextChanges) {
        jobs.push(axiosInstance.patch(`/videos/${video.id}/details`, body));
      }
      if (hasThumbChange) {
        const fd = new FormData();
        fd.append('thumbnail', newThumbFile);
        jobs.push(
          axiosInstance.post(`/videos/${video.id}/thumbnail`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }),
        );
      }
      await Promise.all(jobs);
      toast.success(
        hasThumbChange && hasTextChanges
          ? 'Details + thumbnail updated.'
          : hasThumbChange
            ? 'Thumbnail updated.'
            : 'Details updated.',
      );
      onSaved && onSaved();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to update.';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      data-testid="edit-details-modal"
    >
      <div
        className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="text-white font-semibold text-base">Edit video details</h3>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-white"
            aria-label="Close"
            data-testid="edit-details-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div>
            <Label htmlFor="edit-title" className="text-zinc-300 text-xs">Title</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              className="bg-zinc-900 border-zinc-800 text-white mt-1.5"
              data-testid="edit-title-input"
            />
          </div>

          <div>
            <Label htmlFor="edit-genre" className="text-zinc-300 text-xs">Genre</Label>
            <Select value={genre} onValueChange={setGenre}>
              <SelectTrigger
                id="edit-genre"
                className="bg-zinc-900 border-zinc-800 text-white h-10 mt-1.5"
                data-testid="edit-genre-select"
              >
                <SelectValue placeholder="Select genre" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                {GENRES.map(g => (
                  <SelectItem key={g} value={g} className="text-white hover:bg-zinc-800">
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="edit-description" className="text-zinc-300 text-xs">Description</Label>
            <textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Tell people what this track is about…"
              className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2 mt-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-fuchsia-500/50"
              data-testid="edit-description-input"
            />
            <div className="text-[10px] text-zinc-500 mt-1 text-right">
              {(description || '').length} / 2000
            </div>
          </div>

          {/* Thumbnail replacement — replaces the creator's custom_thumbnail_url
              in-place at the same storage path. All UI surfaces (Home,
              Discover, Pulse, Studio, Admin, Player, OG share previews) read
              via resolveThumbnailUrl() which prefers custom_thumbnail_url, so
              the new image shows everywhere within a page refresh.
              Two capture paths:
                1. Upload a file (JPG/PNG/WebP <8MB)
                2. Grab a frame from the actual video — same iOS-safe
                   pixel-luminance capture flow as the upload page. */}
          <div>
            <Label className="text-zinc-300 text-xs">Thumbnail</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-3">
              <div className="aspect-video bg-zinc-900 rounded-md overflow-hidden border border-zinc-800">
                <img
                  src={newThumbPreview || resolveThumbnailUrl(video)}
                  alt="Thumbnail preview"
                  className="w-full h-full object-cover"
                  data-testid="edit-thumbnail-preview"
                />
              </div>
              <div className="flex flex-col justify-center gap-2">
                <Button
                  onClick={() => thumbInputRef.current?.click()}
                  variant="outline"
                  className="bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border-zinc-700 text-xs h-8"
                  data-testid="edit-thumbnail-pick-btn"
                >
                  {newThumbFile ? 'Change image…' : 'Upload new image'}
                </Button>
                {playbackUrl && (
                  <Button
                    onClick={() => setPreviewActive((v) => !v)}
                    variant="outline"
                    className="bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border-zinc-700 text-xs h-8"
                    data-testid="edit-thumbnail-capture-toggle"
                  >
                    {previewActive ? 'Hide video' : 'Capture from video'}
                  </Button>
                )}
                {newThumbFile && (
                  <button
                    type="button"
                    onClick={() => {
                      if (newThumbPreview) URL.revokeObjectURL(newThumbPreview);
                      setNewThumbFile(null);
                      setNewThumbPreview(null);
                    }}
                    className="text-[11px] text-zinc-400 hover:text-white underline underline-offset-2"
                    data-testid="edit-thumbnail-reset-btn"
                  >
                    Keep current
                  </button>
                )}
                <p className="text-[10px] text-zinc-500 leading-tight">
                  JPG, PNG, or WebP · under 8 MB. Doesn&apos;t change the video itself.
                </p>
              </div>
            </div>
            {/* Frame-capture mini-player. Only mounted when the creator opts
                into it via "Capture from video", so we don't force an HLS
                download on modal open. */}
            {previewActive && playbackUrl && (
              <div className="mt-3 rounded-md border border-zinc-800 bg-black overflow-hidden">
                <video
                  ref={videoPreviewRef}
                  className="w-full aspect-video bg-black"
                  playsInline
                  muted
                  controls={false}
                  onPlay={() => setPreviewIsPlaying(true)}
                  onPause={() => setPreviewIsPlaying(false)}
                  data-testid="edit-thumbnail-video"
                />
                <div className="flex items-center gap-2 p-2 bg-zinc-950/80">
                  <Button
                    onClick={togglePreviewPlayback}
                    variant="ghost"
                    className="text-white hover:bg-zinc-800 h-8 px-3"
                    data-testid="edit-thumbnail-play-btn"
                  >
                    {previewIsPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    <span className="ml-1.5 text-xs">{previewIsPlaying ? 'Pause' : 'Play'}</span>
                  </Button>
                  <Button
                    onClick={captureCurrentFrame}
                    className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white h-8 px-3 text-xs ml-auto"
                    data-testid="edit-thumbnail-capture-btn"
                  >
                    <Camera className="w-3.5 h-3.5 mr-1.5" />
                    Capture this frame
                  </Button>
                </div>
                <p className="text-[10px] text-zinc-500 leading-tight p-2 pt-0">
                  Tip: on mobile, hit Play for a second first — some browsers
                  return a black frame if the video hasn&apos;t actually played yet.
                </p>
              </div>
            )}
            {/* Hidden canvas used by captureCurrentFrame to rasterize the
                current <video> frame into a JPEG blob. */}
            <canvas ref={canvasRef} className="hidden" />
            <input
              ref={thumbInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={pickThumbnail}
              className="hidden"
              data-testid="edit-thumbnail-input"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-zinc-800 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            className="text-zinc-300 hover:text-white hover:bg-zinc-800"
            data-testid="edit-details-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-fuchsia-500 hover:bg-fuchsia-600 text-white"
            data-testid="edit-details-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};
